import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Gift, Star, Crown, Heart, Check, X } from 'lucide-react'

interface RewardData {
  id: string
  name: string
  description: string
  reward_type: 'discount' | 'free_item' | 'points' | 'special'
  reward_value: string
  image_url?: string
  admin_approval_required: boolean
}

interface ReferralRewardAssignment {
  id: string
  tenant_id: string
  reward_id: string
  referral_count: number
  is_active: boolean
  reward: RewardData
}

interface CustomerRedemption {
  id: string
  reward_assignment_id: string
  status: 'pending' | 'approved' | 'rejected'
  redemption_code: string
  redeemed_at: string
}

interface RewardDetailModalProps {
  isOpen: boolean
  onClose: () => void
  rewardAssignment: ReferralRewardAssignment | null
  canRedeem: boolean
  redemptionStatus: CustomerRedemption | null
  onRedeem: (rewardAssignment: ReferralRewardAssignment) => void
  redeeming: boolean
  currentStamps: number
}

const getIcon = (rewardType: string) => {
  switch (rewardType) {
    case 'discount': return Star
    case 'free_item': return Gift
    case 'points': return Crown
    case 'special': return Heart
    default: return Gift
  }
}

const getTypeLabel = (rewardType: string) => {
  switch (rewardType) {
    case 'discount': return 'Rabatt'
    case 'free_item': return 'Kostenloser Artikel'
    case 'points': return 'Punkte'
    case 'special': return 'Sonderangebot'
    default: return 'Belohnung'
  }
}

const RewardDetailModal = ({
  isOpen,
  onClose,
  rewardAssignment,
  canRedeem,
  redemptionStatus,
  onRedeem,
  redeeming,
  currentStamps
}: RewardDetailModalProps) => {
  if (!rewardAssignment) return null

  const IconComponent = getIcon(rewardAssignment.reward.reward_type)
  const typeLabel = getTypeLabel(rewardAssignment.reward.reward_type)

  const getStatusButton = () => {
    if (redemptionStatus) {
      return (
        <Button
          size="sm"
          variant="secondary"
          disabled
          className="bg-muted text-muted-foreground text-xs px-3 py-1"
        >
          Bereits beantragt
        </Button>
      )
    }

    if (canRedeem) {
      return (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => onRedeem(rewardAssignment)}
          disabled={redeeming}
          className="bg-primary/10 text-primary hover:bg-primary/20 text-xs px-3 py-1"
        >
          {redeeming ? "Wird beantragt..." : "Beantragen"}
        </Button>
      )
    }

    return (
      <Button
        size="sm"
        variant="secondary"
        disabled
        className="bg-muted text-muted-foreground text-xs px-3 py-1"
      >
        Beantragen
      </Button>
    )
  }

  const descriptionId = rewardAssignment.reward.description ? "reward-description" : undefined;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-2xl max-h-[90vh] overflow-y-auto" 
        aria-describedby={descriptionId}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center mb-4">
            {rewardAssignment.reward.name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* Reward Image/Icon */}
          <div className="flex justify-center">
            {rewardAssignment.reward.image_url ? (
              <div className="relative">
                <img 
                  src={rewardAssignment.reward.image_url} 
                  alt={rewardAssignment.reward.name}
                  className="w-64 h-64 object-cover rounded-lg shadow-lg"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none'
                    e.currentTarget.nextElementSibling?.setAttribute('style', 'display: flex')
                  }}
                />
                <div 
                  className="w-64 h-64 rounded-lg border-2 border-primary bg-primary/10 items-center justify-center"
                  style={{ display: 'none' }}
                >
                  <IconComponent className="h-24 w-24 text-primary" />
                </div>
              </div>
            ) : (
              <div className="w-64 h-64 rounded-lg border-2 border-primary bg-primary/10 flex items-center justify-center">
                <IconComponent className="h-24 w-24 text-primary" />
              </div>
            )}
          </div>

          {/* Reward Details */}
          <div className="space-y-4">
            <div className="flex justify-between items-start">
              <div className="space-y-2">
                <Badge variant="secondary" className="text-sm">
                  {typeLabel}
                </Badge>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Benötigte Empfehlungen:</span>
                  <span className="font-semibold text-lg text-primary">
                    {rewardAssignment.referral_count}
                  </span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-muted-foreground">Wert</div>
                <div className="text-lg font-semibold text-primary">
                  {rewardAssignment.reward.reward_value}
                </div>
              </div>
            </div>

            {/* Description */}
            {rewardAssignment.reward.description && (
              <div className="space-y-2">
                <h3 className="font-semibold text-lg">Beschreibung</h3>
                <p id="reward-description" className="text-muted-foreground leading-relaxed">
                  {rewardAssignment.reward.description}
                </p>
              </div>
            )}

            {/* Admin Approval Notice */}
            {rewardAssignment.reward.admin_approval_required && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex items-start gap-2">
                  <Heart className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium">Administratorbestätigung erforderlich</p>
                    <p className="mt-1">Diese Prämie muss vom Administrator bestätigt werden, bevor sie verwendet werden kann.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Current Progress */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Ihre Empfehlungen</span>
                <span>{currentStamps} / {rewardAssignment.referral_count}</span>
              </div>
              <div className="w-full bg-muted rounded-full h-2">
                <div 
                  className="bg-primary h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${Math.min((currentStamps / rewardAssignment.referral_count) * 100, 100)}%`
                  }}
                ></div>
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className="pt-4 border-t">
            {getStatusButton()}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default RewardDetailModal